/**
 * BidNotesProposal
 * ----------------
 * Merges the division scope, the priced BOM and the commercial terms into one
 * signable proposal. Live totals here, the signature block in the PDF.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, FileSignature, Download, Save, AlertTriangle, CheckCircle2, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";
import { openAuthedDoc } from "@/lib/openAuthedDoc";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DISPLAY = [
  { id: "line_item", label: "Line item" },
  { id: "division", label: "By division" },
  { id: "lump_sum", label: "Lump sum" },
];

export const BidNotesProposal = ({ noteId, token }) => {
  const [pr, setPr] = useState(null);
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bid-desk/notes/${noteId}/proposal`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPr(r.data);
      setT(r.data.terms);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assemble the proposal");
    }
  }, [noteId, token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/bid-desk/notes/${noteId}/proposal`, {
        ...t,
        validity_days: Number(t.validity_days) || 30,
        contingency_pct: Number(t.contingency_pct) || 0,
        tax_pct: Number(t.tax_pct) || 0,
        freight: Number(t.freight) || 0,
        bond_pct: Number(t.bond_pct) || 0,
        extra_exclusions: (t.extra_exclusions_text ?? (t.extra_exclusions || []).join("\n"))
          .split("\n").map((x) => x.trim()).filter(Boolean),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPr(r.data.proposal);
      setT(r.data.proposal.terms);
      toast.success("Proposal terms saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the terms");
    } finally { setBusy(false); }
  };

  if (!pr || !t) {
    return <p className="text-slate-500 inline-flex items-center gap-2 py-5">
      <Loader2 className="w-4 h-4 animate-spin" /> Assembling the proposal…</p>;
  }

  const p = pr.pricing;
  const doc = (fmt) => openAuthedDoc(`/bid-desk/notes/${noteId}/proposal/render`, {
    token, fmt,
    filename: fmt === "docx" ? `SterileMate-Proposal-${pr.proposal_number}.docx` : undefined,
  });
  const set = (k) => (e) => setT({ ...t, [k]: e.target.value });

  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="notes-proposal">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-black text-slate-900 inline-flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-indigo-600" /> Signable proposal
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Scope, priced BOM and terms merged into one document with a signature block ·
            {" "}<span className="font-mono">{pr.proposal_number}</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => doc("html")} data-testid="proposal-preview"
            className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50">
            <FileText className="w-3.5 h-3.5 mr-1" /> Preview
          </button>
          <button onClick={() => doc("pdf")} data-testid="proposal-pdf"
            className="inline-flex items-center h-8 px-2.5 rounded-md bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700">
            <Download className="w-3.5 h-3.5 mr-1" /> Proposal PDF
          </button>
          <button onClick={() => doc("docx")} data-testid="proposal-docx"
            className="inline-flex items-center h-8 px-2.5 rounded-md border border-slate-300 text-xs hover:bg-slate-50">
            <Download className="w-3.5 h-3.5 mr-1" /> Word
          </button>
        </div>
      </div>

      {pr.blockers.length > 0 ? (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="proposal-blockers">
          <p className="font-bold inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Before you send this:
          </p>
          <ul className="list-disc ml-5 mt-1">
            {pr.blockers.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
          {p.unpriced.length > 0 && (
            <p className="mt-1 text-amber-800">
              Unpriced: {p.unpriced.slice(0, 4).join("; ")}
              {p.unpriced.length > 4 ? ` +${p.unpriced.length - 4} more` : ""}
            </p>
          )}
        </div>
      ) : (
        <p className="mb-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 inline-flex items-center gap-1.5"
          data-testid="proposal-ready">
          <CheckCircle2 className="w-3.5 h-3.5" /> Scope and pricing are complete — ready to issue.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1.5">
            Price roll-up ({p.lines.length} BOM lines · {p.markup_pct}% markup)
          </p>
          <table className="w-full text-sm border border-slate-200 rounded">
            <tbody className="divide-y divide-slate-100">
              {p.by_division.map((d) => (
                <tr key={d.category} data-testid={`proposal-div-${d.category}`}>
                  <td className="px-2.5 py-1.5 text-slate-700">{d.category}</td>
                  <td className="px-2.5 py-1.5 text-right tabular-nums">{money(d.amount)}</td>
                </tr>
              ))}
              <Line label="Subtotal" value={p.subtotal} strong />
              {p.contingency > 0 && <Line label="Contingency" value={p.contingency} />}
              {p.freight > 0 && <Line label="Freight" value={p.freight} />}
              {p.bond > 0 && <Line label="Bond" value={p.bond} />}
              {p.tax > 0 && <Line label="Sales tax" value={p.tax} />}
              <tr className="bg-slate-900 text-white">
                <td className="px-2.5 py-2 font-black">Total proposed price</td>
                <td className="px-2.5 py-2 text-right font-black tabular-nums" data-testid="proposal-total">
                  {money(p.total)}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="text-[11px] text-slate-500 mt-1.5">
            Cost basis {money(p.cost_basis)} · margin {money(p.subtotal - p.cost_basis)}
          </p>
        </div>

        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Proposal number">
              <Input value={t.proposal_number || ""} onChange={set("proposal_number")}
                placeholder={pr.proposal_number} className="h-8 text-xs" data-testid="proposal-number" />
            </Field>
            <Field label="Valid for (days)">
              <Input type="number" value={t.validity_days} onChange={set("validity_days")}
                className="h-8 text-xs" data-testid="proposal-validity" />
            </Field>
            <Field label="Contingency %">
              <Input type="number" value={t.contingency_pct} onChange={set("contingency_pct")} className="h-8 text-xs" />
            </Field>
            <Field label="Freight $">
              <Input type="number" value={t.freight} onChange={set("freight")} className="h-8 text-xs" />
            </Field>
            <Field label="Bond %">
              <Input type="number" value={t.bond_pct} onChange={set("bond_pct")} className="h-8 text-xs" />
            </Field>
            <Field label="Sales tax %">
              <Input type="number" value={t.tax_pct} onChange={set("tax_pct")}
                className="h-8 text-xs" data-testid="proposal-tax" />
            </Field>
          </div>

          <Field label="How pricing is shown to the owner">
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
              {DISPLAY.map((o) => (
                <button key={o.id} onClick={() => setT({ ...t, pricing_display: o.id })}
                  className={`px-2.5 py-1 text-xs font-bold ${
                    t.pricing_display === o.id ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  data-testid={`proposal-display-${o.id}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Payment terms">
            <Textarea value={t.payment_terms || ""} onChange={set("payment_terms")} rows={2}
              className="text-xs" data-testid="proposal-payment-terms" />
          </Field>
          <Field label="Additional exclusions (one per line)">
            <Textarea
              value={t.extra_exclusions_text ?? (t.extra_exclusions || []).join("\n")}
              onChange={(e) => setT({ ...t, extra_exclusions_text: e.target.value })}
              rows={2} className="text-xs" data-testid="proposal-exclusions" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Prepared by">
              <Input value={t.prepared_by || ""} onChange={set("prepared_by")} className="h-8 text-xs" />
            </Field>
            <Field label="Owner signer (optional)">
              <Input value={t.signer_name || ""} onChange={set("signer_name")}
                className="h-8 text-xs" placeholder="printed on the signature line" />
            </Field>
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={save} disabled={busy}
              className="bg-slate-900 hover:bg-slate-800" data-testid="proposal-save">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save terms
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const Line = ({ label, value, strong }) => (
  <tr className={strong ? "bg-slate-50" : ""}>
    <td className={`px-2.5 py-1.5 text-right ${strong ? "font-bold text-slate-900" : "text-slate-600"}`}>{label}</td>
    <td className="px-2.5 py-1.5 text-right tabular-nums font-semibold">{money(value)}</td>
  </tr>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">{label}</span>
    {children}
  </label>
);

export default BidNotesProposal;
